import type { Customer, CustomerAddress, WovarCheckout } from "@commercekit/api";
import type { Dispatch, FC, SetStateAction } from "react";
import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import type { PasswordValidator } from "~/lib/validator";
import { addressZ, businessInfoZ, deliveryLocationZ, signupZ } from "~/lib/validator";

import { useCheckout, useCheckoutCountry, useCheckoutPayment, useCheckoutSignup } from "~/lib/hooks/useCheckout";

import { useCustomer, useDeliveryLocations, useShippingAddress } from "~/lib/hooks/useCustomer";

import { IsDeliveryToAddress, IsSameAddress, IsSignUp } from "@commercekit/shared/type";
import { withZod } from "@remix-validated-form/with-zod";
import { z } from "zod";
import { numberValidation, useLocale } from "~/index";
import { useHouseNumber } from "~/lib/hooks/useHouseNumber";

const CheckoutAddressCtx = createContext<{
  billingAddress: CustomerAddress | null;
  setBillingAddress: Dispatch<SetStateAction<CustomerAddress | null>>;
  shippingAddress: CustomerAddress | null;
  setShippingAddress: Dispatch<SetStateAction<CustomerAddress | null>>;
  isSameAddress: IsSameAddress;
  setIsSameAddress: Dispatch<SetStateAction<IsSameAddress>>;
  billingCountryCode: string | undefined;
  setBillingCountryCode: Dispatch<SetStateAction<string | undefined>>;
  shippingCountryCode: string | undefined;
  setShippingCountryCode: Dispatch<SetStateAction<string | undefined>>;
  isManualAddress: boolean;
  setIsManualAddress: Dispatch<SetStateAction<boolean>>;
  deliverToNeighbor: boolean;
  setDeliverToNeighbor: Dispatch<SetStateAction<boolean>>;
  isBusinessAccount: boolean;
  setIsBusinessAccount: Dispatch<SetStateAction<boolean>>;
  isDeliveryToAddress: IsDeliveryToAddress;
  setIsDeliveryToAddress: Dispatch<SetStateAction<IsDeliveryToAddress>>;
  deliveryLocationsLoading: boolean;
  deliveryLocations: { name: string; value: string }[];
  deliveryLocation: string;
  setDeliveryLocation: Dispatch<SetStateAction<string>>;
  setParamsOfDeliveryLocations: Dispatch<SetStateAction<{ countryCode: string; postalCode: string }>>;
}>({
  billingAddress: null,
  setBillingAddress: () => {},
  shippingAddress: null,
  setShippingAddress: () => {},
  isSameAddress: IsSameAddress.YES,
  setIsSameAddress: () => {},
  billingCountryCode: undefined,
  setBillingCountryCode: () => {},
  shippingCountryCode: undefined,
  setShippingCountryCode: () => {},
  isManualAddress: false,
  setIsManualAddress: () => {},
  deliverToNeighbor: true,
  setDeliverToNeighbor: () => {},
  isBusinessAccount: false,
  setIsBusinessAccount: () => {},
  isDeliveryToAddress: IsDeliveryToAddress.YES,
  setIsDeliveryToAddress: () => {},
  deliveryLocations: [],
  deliveryLocationsLoading: false,
  deliveryLocation: "",
  setDeliveryLocation: () => {},
  setParamsOfDeliveryLocations: () => {},
});

export const CheckoutAddressCtxProvider: FC<React.PropsWithChildren> = ({ children }) => {
  const { checkout } = useCheckout();
  const { customer } = useCustomer();
  const { locale } = useLocale();

  const { checkoutDefaultAddress } = useCheckoutCountry(checkout?.shippingAddress?.countryCode || "");

  const isB2B = useMemo(() => customer?.type && customer?.type !== "B2C", [customer?.type]);

  const isBusinessFromCheckout = useMemo(
    () =>
      Boolean(
        checkout?.billingAddress?.company ||
          checkout?.billingAddress?.vatNumber ||
          checkout?.billingAddress?.kvkNumber ||
          checkout?.shippingAddress?.vatNumber ||
          checkout?.shippingAddress?.kvkNumber
      ),
    [checkout]
  );

  const isDifferentCustomerAddresses = (customer: Customer) =>
    ["firstName", "lastName", "address1", "address2", "city", "postalCode"].some((field) => {
      return customer.defaultBillingAddress[field] !== customer.defaultShippingAddress[field];
    });

  const isDifferentCheckoutAddresses = (checkout: WovarCheckout) => {
    if (!checkout?.shippingAddress?.email || !checkout?.shippingAddress?.address1) return false;
    return ["firstName", "lastName", "address1", "address2", "city", "postalCode"].some((field) => {
      return checkout?.billingAddress?.[field] !== checkout?.shippingAddress?.[field];
    });
  };

  const useSameAddress = useMemo(() => {
    if (customer?.defaultBillingAddress == null || customer?.defaultShippingAddress == null) {
      return IsSameAddress.YES;
    }

    if (isDifferentCustomerAddresses(customer) || isDifferentCheckoutAddresses(checkout)) {
      return IsSameAddress.NO;
    }

    return IsSameAddress.YES;
  }, [customer, checkout]);

  const [billingAddress, setBillingAddress] = useState<CustomerAddress | null>(
    (checkout?.billingAddress as CustomerAddress) || null
  );
  const { shippingAddressDefault } = useShippingAddress();
  const [shippingAddress, setShippingAddress] = useState<CustomerAddress | null>(
    (checkout?.shippingAddress as CustomerAddress) || shippingAddressDefault
  );
  const [billingCountryCode, setBillingCountryCode] = useState<string>(checkoutDefaultAddress?.value || locale);
  const [shippingCountryCode, setShippingCountryCode] = useState<string>(checkoutDefaultAddress?.value || locale);
  const [isSameAddress, setIsSameAddress] = useState<IsSameAddress>(useSameAddress);

  // WOV-1840: when "same address" is on, mirror billing → shipping country so the
  // two states never diverge. Without this, a customer who unticks "same address",
  // changes billing country, then re-ticks would have shippingCountryCode stuck on
  // the original value while the visible (billing) dropdown shows the new one —
  // producing channel/country mismatched orders.
  useEffect(() => {
    if (isSameAddress === IsSameAddress.YES && shippingCountryCode !== billingCountryCode) {
      setShippingCountryCode(billingCountryCode);
    }
  }, [isSameAddress, billingCountryCode, shippingCountryCode]);
  const [isManualAddress, setIsManualAddress] = useState(false);
  const [deliverToNeighbor, setDeliverToNeighbor] = useState(true);
  const [isBusinessAccount, setIsBusinessAccount] = useState(isB2B || isBusinessFromCheckout);
  const [isDeliveryToAddress, setIsDeliveryToAddress] = useState<IsDeliveryToAddress>(IsDeliveryToAddress.YES);
  const { isLoading, deliveryLocations, setParamsOfDeliveryLocations } = useDeliveryLocations(shippingAddress);
  const [deliveryLocation, setDeliveryLocation] = useState("");

  return (
    <CheckoutAddressCtx.Provider
      value={{
        billingAddress,
        setBillingAddress,
        shippingAddress,
        setShippingAddress,
        isSameAddress,
        setIsSameAddress,
        isManualAddress,
        setIsManualAddress,
        billingCountryCode,
        setBillingCountryCode,
        shippingCountryCode,
        setShippingCountryCode,
        deliverToNeighbor,
        setDeliverToNeighbor,
        isBusinessAccount,
        setIsBusinessAccount,
        isDeliveryToAddress,
        setIsDeliveryToAddress,
        deliveryLocationsLoading: isLoading,
        deliveryLocations,
        deliveryLocation,
        setDeliveryLocation,
        setParamsOfDeliveryLocations,
      }}
    >
      {children}
    </CheckoutAddressCtx.Provider>
  );
};

export const useCheckoutAddress = () => useContext(CheckoutAddressCtx);

export const useShipmentDataAsGuest = (passwordValidator: PasswordValidator) => {
  const { isSignup } = useCheckoutSignup();
  const { phoneCodeCountries } = useCheckout();
  const { isSameAddress, isManualAddress, isDeliveryToAddress, isBusinessAccount } = useCheckoutAddress();
  const includeHouseNumber = useHouseNumber();
  const { paymentMethod } = useCheckoutPayment();

  const validator = React.useMemo(() => {
    // Helper function to create base address schema
    const createBaseSchema = (withKvk: boolean) => {
      let schema = addressZ("billing", includeHouseNumber, isManualAddress);

      // Add payment method validation
      schema = { ...schema, paymentMethods: z.any() };

      // Add shipping address if different from billing
      if (isSameAddress === IsSameAddress.NO) {
        schema = { ...schema, ...addressZ("shipping", includeHouseNumber, isManualAddress) };
      }

      // Add business info if business account
      if (isBusinessAccount) {
        schema = { ...schema, ...businessInfoZ("billing", withKvk) };
      }

      // Add signup fields if signing up
      if (isSignup === IsSignUp.YES) {
        schema = { ...schema, ...signupZ(passwordValidator, true) };
      }

      // Add delivery location if not delivering to address
      if (isDeliveryToAddress === IsDeliveryToAddress.NO) {
        Object.assign(schema, { deliveryLocation: deliveryLocationZ });
      }

      return schema;
    };

    // Helper function to add all validation refinements
    const createValidatorWithRefinements = (schema: any) => {
      const baseValidator = z
        .object(schema)

        .superRefine((data, ctx) => {
          // Phone validation
          const selectedCountry = phoneCodeCountries.find((c) => c.code === data.phoneCountryCode);
          const { isValid, message } = numberValidation(selectedCountry, data["billing_phone"]);
          if (!isValid) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message,
              path: ["billing_phone"],
            });
          }
        })
        .refine((data) => data.paymentMethods, {
          // Payment method validation
          message: "payment_method_required_message",
          path: ["paymentMethods"],
        });

      // Add password confirmation for signup
      if (isSignup === IsSignUp.YES) {
        return baseValidator.refine((data) => data.password === data.confirmPassword, {
          message: "form_message_confirm_password_not_match",
          path: ["confirmPassword"],
        });
      }

      return baseValidator;
    };

    // Create schemas for both regular and KVK cases
    const baseSchema = createBaseSchema(false);
    const kvkSchema = createBaseSchema(true);

    return [createValidatorWithRefinements(baseSchema), createValidatorWithRefinements(kvkSchema)];
  }, [
    isDeliveryToAddress,
    isSameAddress,
    isManualAddress,
    isSignup,
    passwordValidator,
    phoneCodeCountries,
    isBusinessAccount,
    includeHouseNumber,
    paymentMethod.current,
  ]);

  return {
    validator: withZod(validator[0]),
    validatorWithKvk: withZod(validator[1]),
    // Remove the step-based fetcher and just return simple loading state
    loading: false,
  };
};
